"use client";
import { useEffect, useState, useRef } from "react";
import { onCelebration, type CelebrationEvent, type CelebrationKind } from "@/lib/celebration";

type ActiveEvent = CelebrationEvent & { id: string };

export function CelebrationHost() {
  const [active, setActive] = useState<ActiveEvent | null>(null);
  const idRef = useRef(0);
  const lastShownRef = useRef<{ text: string; at: number } | null>(null);

  useEffect(() => {
    return onCelebration((e) => {
      const now = Date.now();
      if (lastShownRef.current && lastShownRef.current.text === e.text && now - lastShownRef.current.at < 1500) {
        return;
      }
      lastShownRef.current = { text: e.text, at: now };
      const id = String(++idRef.current);
      setActive({ ...e, id });
      const dur = e.durationMs ?? 4200;
      window.setTimeout(() => {
        setActive((cur) => (cur && cur.id === id ? null : cur));
      }, dur);
    });
  }, []);

  if (!active) return null;
  const kind = active.kind ?? "confetti";
  return (
    <div className="pointer-events-none fixed inset-0 z-[200] overflow-hidden">
      <EffectLayer kind={kind} key={active.id} />
      <MessageCenter text={active.text} title={active.title} source={active.source} />
    </div>
  );
}

function MessageCenter({ text, title, source }: { text: string; title?: string; source?: "self" | "team" }) {
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <div className="animate-celebrate-pop relative max-w-[90vw] rounded-3xl border border-white/40 bg-gradient-to-br from-white/95 via-white/85 to-amber-50/90 px-10 py-7 text-center shadow-[0_30px_80px_-20px_rgba(245,158,11,0.55)] backdrop-blur-md">
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-3 py-1 text-[11px] font-bold tracking-widest text-white shadow-md">
          {source === "team" ? "🎉 团队任务完成" : "✨ 任务完成"}
        </div>
        {title && (
          <div className="mb-2 truncate text-[12px] font-medium uppercase tracking-wider text-slate-500">
            {title.length > 28 ? title.slice(0, 28) + "…" : title}
          </div>
        )}
        <div className="bg-gradient-to-r from-amber-600 via-rose-500 to-fuchsia-600 bg-clip-text text-3xl font-extrabold leading-tight text-transparent sm:text-4xl">
          {text}
        </div>
        <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
          <span className="inline-block h-1.5 w-1.5 animate-celebrate-bounce rounded-full bg-amber-500" style={{ animationDelay: "0ms" }} />
          <span className="inline-block h-1.5 w-1.5 animate-celebrate-bounce rounded-full bg-orange-500" style={{ animationDelay: "120ms" }} />
          <span className="inline-block h-1.5 w-1.5 animate-celebrate-bounce rounded-full bg-rose-500" style={{ animationDelay: "240ms" }} />
        </div>
      </div>
    </div>
  );
}

function EffectLayer({ kind }: { kind: CelebrationKind }) {
  switch (kind) {
    case "confetti":  return <ConfettiEffect />;
    case "fireworks": return <FireworksEffect />;
    case "starburst": return <StarburstEffect />;
    case "balloon":   return <BalloonEffect />;
    case "sparkle":   return <SparkleEffect />;
    case "ribbon":    return <RibbonEffect />;
    case "snowfall":  return <SnowfallEffect />;
    case "ripple":    return <RippleEffect />;
    case "medal":     return <MedalEffect />;
    case "rocket":    return <RocketEffect />;
    case "rainbow":   return <RainbowEffect />;
    case "phoenix":   return <PhoenixEffect />;
  }
}

const PALETTE = [
  "#f43f5e", "#fb7185", "#f59e0b", "#facc15", "#84cc16",
  "#10b981", "#06b6d4", "#3b82f6", "#6366f1", "#a855f7",
  "#d946ef", "#ec4899",
];

function ConfettiEffect() {
  return (
    <div className="absolute inset-0">
      {Array.from({ length: 80 }).map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.6;
        const dur = 2.6 + Math.random() * 1.2;
        const color = PALETTE[Math.floor(Math.random() * PALETTE.length)];
        const rot = Math.random() * 360;
        const w = 6 + Math.random() * 6;
        const h = 10 + Math.random() * 10;
        return (
          <span
            key={i}
            className="absolute top-0 block animate-celebrate-confetti"
            style={{
              left: left + "%",
              width: w + "px",
              height: h + "px",
              background: color,
              transform: "rotate(" + rot + "deg)",
              animationDelay: delay + "s",
              animationDuration: dur + "s",
              borderRadius: Math.random() > 0.5 ? "2px" : "999px",
            }}
          />
        );
      })}
    </div>
  );
}

function FireworksEffect() {
  return (
    <div className="absolute inset-0">
      {Array.from({ length: 5 }).map((_, burst) => {
        const cx = 15 + Math.random() * 70;
        const cy = 20 + Math.random() * 40;
        const delay = burst * 0.5;
        return (
          <div key={burst} className="absolute" style={{ left: cx + "%", top: cy + "%" }}>
            {Array.from({ length: 18 }).map((_, i) => {
              const ang = (i / 18) * Math.PI * 2;
              const dist = 80 + Math.random() * 100;
              const dx = Math.cos(ang) * dist;
              const dy = Math.sin(ang) * dist;
              const color = PALETTE[Math.floor(Math.random() * PALETTE.length)];
              return (
                <span
                  key={i}
                  className="absolute block h-1.5 w-1.5 rounded-full animate-celebrate-firework"
                  style={{
                    background: color,
                    boxShadow: "0 0 8px " + color,
                    ["--cx" as any]: dx + "px",
                    ["--cy" as any]: dy + "px",
                    animationDelay: delay + "s",
                  }}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function StarburstEffect() {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      {Array.from({ length: 8 }).map((_, i) => {
        const ang = (i / 8) * 360;
        const color = PALETTE[i % PALETTE.length];
        return (
          <span
            key={i}
            className="absolute h-3 w-40 origin-left animate-celebrate-starburst rounded-full"
            style={{
              background: "linear-gradient(90deg, " + color + ", transparent)",
              transform: "rotate(" + ang + "deg)",
            }}
          />
        );
      })}
      <div className="absolute h-24 w-24 animate-celebrate-pulse rounded-full bg-gradient-to-br from-amber-300 via-rose-400 to-fuchsia-500 shadow-[0_0_60px_rgba(244,63,94,0.6)]" />
    </div>
  );
}

function BalloonEffect() {
  return (
    <div className="absolute inset-0">
      {Array.from({ length: 14 }).map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 1.2;
        const dur = 3.2 + Math.random() * 1;
        const color = PALETTE[Math.floor(Math.random() * PALETTE.length)];
        return (
          <div key={i} className="absolute bottom-0 animate-celebrate-balloon" style={{ left: left + "%", animationDelay: delay + "s", animationDuration: dur + "s" }}>
            <div className="h-14 w-10 rounded-full shadow-md" style={{ background: color, boxShadow: "inset -6px -6px 12px rgba(0,0,0,0.18)" }} />
            <div className="mx-auto h-0.5 w-0.5 bg-slate-400" />
            <div className="mx-auto h-8 w-px bg-slate-300" />
          </div>
        );
      })}
    </div>
  );
}

function SparkleEffect() {
  return (
    <div className="absolute inset-0">
      {Array.from({ length: 60 }).map((_, i) => {
        const top = Math.random() * 100;
        const left = Math.random() * 100;
        const size = 4 + Math.random() * 14;
        const delay = Math.random() * 1;
        const color = PALETTE[Math.floor(Math.random() * PALETTE.length)];
        return (
          <span
            key={i}
            className="absolute block animate-celebrate-sparkle"
            style={{
              top: top + "%",
              left: left + "%",
              width: size + "px",
              height: size + "px",
              background: color,
              boxShadow: "0 0 " + (size * 1.5) + "px " + color,
              clipPath: "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)",
              animationDelay: delay + "s",
            }}
          />
        );
      })}
    </div>
  );
}

function RibbonEffect() {
  return (
    <div className="absolute inset-0">
      {Array.from({ length: 12 }).map((_, i) => {
        const fromLeft = i % 2 === 0;
        const top = 10 + (i * 7) % 80;
        const color = PALETTE[Math.floor(Math.random() * PALETTE.length)];
        const delay = (i * 0.1) % 1;
        return (
          <span
            key={i}
            className={"absolute h-1.5 w-60 animate-celebrate-ribbon rounded-full " + (fromLeft ? "left-0" : "right-0")}
            style={{
              top: top + "%",
              background: "linear-gradient(" + (fromLeft ? "90deg" : "270deg") + ", " + color + ", transparent)",
              transformOrigin: fromLeft ? "left center" : "right center",
              animationDelay: delay + "s",
            }}
          />
        );
      })}
    </div>
  );
}

function SnowfallEffect() {
  return (
    <div className="absolute inset-0">
      {Array.from({ length: 50 }).map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 1.5;
        const dur = 2.8 + Math.random() * 1.4;
        const size = 8 + Math.random() * 12;
        const color = PALETTE[Math.floor(Math.random() * PALETTE.length)];
        return (
          <span
            key={i}
            className="absolute -top-4 block animate-celebrate-snowfall"
            style={{
              left: left + "%",
              width: size + "px",
              height: size + "px",
              background: color,
              animationDelay: delay + "s",
              animationDuration: dur + "s",
              borderRadius: "50% 0 50% 50%",
              transform: "rotate(" + (Math.random() * 360) + "deg)",
              opacity: 0.85,
            }}
          />
        );
      })}
    </div>
  );
}

function RippleEffect() {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      {[0, 0.4, 0.8, 1.2, 1.6].map((d, i) => (
        <span
          key={i}
          className="absolute rounded-full border-4 animate-celebrate-ripple"
          style={{
            width: "10vmin",
            height: "10vmin",
            borderColor: PALETTE[i % PALETTE.length],
            animationDelay: d + "s",
          }}
        />
      ))}
    </div>
  );
}

function MedalEffect() {
  return (
    <div className="absolute inset-0">
      {Array.from({ length: 6 }).map((_, i) => {
        const left = 10 + (i * 14);
        const delay = i * 0.15;
        const colors = ["#facc15", "#fb923c", "#f43f5e", "#a855f7", "#3b82f6", "#10b981"];
        return (
          <div key={i} className="absolute -top-20 animate-celebrate-medal" style={{ left: left + "%", animationDelay: delay + "s" }}>
            <div className="h-10 w-1 bg-rose-700 mx-auto" />
            <div
              className="h-14 w-14 rounded-full shadow-lg flex items-center justify-center text-white text-2xl font-bold"
              style={{ background: "radial-gradient(circle at 30% 30%, #fef3c7, " + colors[i % colors.length] + ")" }}
            >★</div>
          </div>
        );
      })}
    </div>
  );
}

function RocketEffect() {
  return (
    <div className="absolute inset-0">
      {Array.from({ length: 4 }).map((_, i) => {
        const left = 20 + i * 18;
        const delay = i * 0.3;
        return (
          <div key={i} className="absolute bottom-0 animate-celebrate-rocket" style={{ left: left + "%", animationDelay: delay + "s" }}>
            <div className="text-4xl">🚀</div>
            <div className="mx-auto h-12 w-2 animate-celebrate-flame rounded-b-full bg-gradient-to-b from-amber-400 via-orange-500 to-transparent" />
          </div>
        );
      })}
    </div>
  );
}

function RainbowEffect() {
  const colors = ["#ef4444", "#f59e0b", "#facc15", "#10b981", "#06b6d4", "#3b82f6", "#a855f7"];
  return (
    <div className="absolute inset-0 flex items-start justify-center">
      <div className="relative h-32 w-[120vw] -translate-x-[10vw] animate-celebrate-rainbow">
        {colors.map((c, i) => (
          <div
            key={i}
            className="absolute inset-x-0 rounded-full"
            style={{
              top: i * 6 + "px",
              height: "16px",
              background: c,
              opacity: 0.75,
              filter: "blur(1px)",
            }}
          />
        ))}
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/30 to-transparent" />
    </div>
  );
}

function PhoenixEffect() {
  return (
    <div className="absolute inset-0">
      <div className="absolute inset-0 animate-celebrate-flash bg-gradient-to-b from-rose-500/30 via-amber-400/20 to-orange-500/0" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className="animate-celebrate-phoenix text-[160px] leading-none">🦅</div>
      </div>
      {Array.from({ length: 40 }).map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.8;
        const color = PALETTE[Math.floor(Math.random() * PALETTE.length)];
        return (
          <span
            key={i}
            className="absolute bottom-0 block h-2 w-2 animate-celebrate-sparkle rounded-full"
            style={{
              left: left + "%",
              background: color,
              boxShadow: "0 0 8px " + color,
              animationDelay: delay + "s",
            }}
          />
        );
      })}
    </div>
  );
}